import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { CREATE_PROPERTY } from '../graphql/mutations';
import { GET_USERS, GET_PROPERTIES } from '../graphql/queries';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { navigateBackToProperties } from './Properties';
import TeamAssignment from '../components/properties/TeamAssignment';
import { buildPropertyManagementInput } from '../components/properties/PropertyManagementAssignment';

// Property Area options
const PROPERTY_AREAS = [
  { value: 'KIAWAH', label: 'Kiawah Island' },
  { value: 'ISLE_OF_PALMS', label: 'Isle of Palms' },
  { value: 'ISLE_OF_PALMS_WILD_DUNES', label: 'Isle of Palms: Wild Dunes' },
  { value: 'SEABROOK_ISLAND', label: 'Seabrook Island' }
];

// Base input styles - replace all input field classes with this improved version
const baseInputStyles = "px-4 py-2.5 h-11 w-full border border-gray-200 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:border-gray-200 text-sm transition-colors";

// Money input styles with padding for $ symbol
const moneyInputStyles = "pl-8 pr-4 py-2.5 h-11 w-full border border-gray-200 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:border-gray-200 text-sm transition-colors";

// Textarea styles
const textareaStyles = "px-4 py-3 w-full border border-gray-200 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:border-gray-200 text-sm transition-colors resize-none";

// Select styles
const selectStyles = "px-4 py-2.5 h-11 w-full border border-gray-200 rounded-md bg-white text-gray-900 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:border-gray-200 text-sm transition-colors appearance-none bg-no-repeat bg-[right_0.5rem_center] bg-[length:1.5em_1.5em] bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22%236B7280%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M10%203a1%201%200%2001.707.293l3%203a1%201%200%2001-1.414%201.414L10%205.414%207.707%207.707a1%201%200%2001-1.414-1.414l3-3A1%201%200%200110%203zm-3.707%209.293a1%201%200%20011.414%200L10%2014.586l2.293-2.293a1%201%200%20011.414%201.414l-3%203a1%201%200%2001-1.414%200l-3-3a1%201%200%20010-1.414z%22%20clip-rule%3D%22evenodd%22%2F%3E%3C%2Fsvg%3E')] pr-10";

export default function CreateProperty() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get the pagination state that was passed when navigating to create
  const returnToPage = location.state?.returnToPage || 1;
  const returnToSearch = location.state?.returnToSearch || '';
  
  // Add query to fetch users
  const { data: usersData } = useQuery(GET_USERS);

  // Filter users to get only property managers
  const propertyManagers = usersData?.users?.filter(user =>
    ['PROPERTY_MANAGER', 'ADMIN', 'GUEST_APP_MANAGER'].includes(user.role) && user.isActive !== false
  ) || [];

  // State for form data
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    area: '',
    checkInTime: '',
    checkOutTime: '',
    entryDetails: '',
    propertyManagementTeamId: '',
    propertyManagerId: '',
    beds: '',
    baths: '',
    housekeepingNotes: '',
    stagingNotes: '',
    propertyManagerNotes: '',
    websiteLink: '',
    price: '',
    payout: '',
    externalId: '',
    cleaningAssignedUserId: '',
    inspectionAssignedUserId: '',
    walkthroughAssignedUserId: '',
    // Always false for custom properties
    integratedWithStreamline: false
  });

  // Create property mutation
  const [createProperty, { loading: createLoading, error: createError }] = useMutation(CREATE_PROPERTY, {
    refetchQueries: [{ query: GET_PROPERTIES }]
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleBackToProperties = () => {
    navigateBackToProperties(navigate, returnToPage, returnToSearch);
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Helper function to handle text fields
    const formatTextField = (value) => {
      if (value === null || value === undefined) return null;
      const trimmed = String(value).trim();
      return trimmed === '' ? null : trimmed;
    };

    // Helper function to handle numeric fields
    const parseNumericField = (value) => {
      if (value === '' || value === null || value === undefined) return null;
      const parsed = Number(value);
      return isNaN(parsed) ? null : parsed;
    };

    const input = {
      name: formatTextField(formData.name),
      address: formatTextField(formData.address),
      city: formatTextField(formData.city),
      state: formatTextField(formData.state),
      zip: formatTextField(formData.zip),
      area: formatTextField(formData.area),
      checkInTime: formatTextField(formData.checkInTime),
      checkOutTime: formatTextField(formData.checkOutTime),
      entryDetails: formatTextField(formData.entryDetails),
      ...buildPropertyManagementInput(formData),
      beds: parseNumericField(formData.beds),
      baths: parseNumericField(formData.baths),
      housekeepingNotes: formatTextField(formData.housekeepingNotes),
      stagingNotes: formatTextField(formData.stagingNotes),
      propertyManagerNotes: formatTextField(formData.propertyManagerNotes),
      websiteLink: formatTextField(formData.websiteLink),
      price: parseNumericField(formData.price),
      payout: parseNumericField(formData.payout),
      externalId: formatTextField(formData.externalId),
      cleaningAssignedUserId: formatTextField(formData.cleaningAssignedUserId),
      inspectionAssignedUserId: formatTextField(formData.inspectionAssignedUserId),
      walkthroughAssignedUserId: formatTextField(formData.walkthroughAssignedUserId),
      integratedWithStreamline: false, // Always false for custom properties
    };

    try {
      await createProperty({
        variables: { input },
        refetchQueries: [
          {
            query: GET_PROPERTIES,
            variables: {
              limit: 10,
              page: 1,
              startDate: new Date().toISOString().split('T')[0],
              endDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
            }
          }
        ]
      });
      
      toast.success('Property created successfully');
      handleBackToProperties();
    } catch (err) {
      console.error('Error creating property:', err);
      toast.error('Failed to create property');
    }
  };

  return (
    <div className="container mx-auto p-4 max-w-7xl">
      {/* Back button */}
      <div className="mb-6">
        <button
          onClick={handleBackToProperties}
          className="inline-flex items-center text-blue-600 hover:text-blue-800 bg-transparent border-none cursor-pointer"
        >
          <ArrowLeft className="mr-1" />
          <span>Back to Properties</span>
        </button>
      </div>

      {/* Title */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          Add New Property
        </h1>
      </div>

      {/* Error message */}
      {createError && (
        <div className="mb-6 rounded-lg bg-red-50 p-4 text-sm text-red-600 border border-red-200">
          {createError.message}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Info Section */}
        <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Basic Information</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Property Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={baseInputStyles}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Street Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={baseInputStyles}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">City</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={baseInputStyles}
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">State</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className={baseInputStyles}
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Zipcode</label>
                <input
                  type="text"
                  name="zip"
                  value={formData.zip}
                  onChange={handleInputChange}
                  className={baseInputStyles}
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Area</label>
                <select
                  id="area"
                  name="area"
                  value={formData.area}
                  onChange={handleInputChange}
                  className={selectStyles}
                >
                  <option value="">Select an area</option>
                  {PROPERTY_AREAS.map((area) => (
                    <option key={area.value} value={area.value}>
                      {area.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Check-in/out Times Section */}
        <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Check-in Information</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Check-in Time</label>
                <input
                  type="text"
                  name="checkInTime"
                  value={formData.checkInTime}
                  onChange={handleInputChange}
                  className={baseInputStyles}
                  placeholder="e.g., 4:00 PM"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Check-out Time</label>
                <input
                  type="text"
                  name="checkOutTime"
                  value={formData.checkOutTime}
                  onChange={handleInputChange}
                  className={baseInputStyles}
                  placeholder="e.g., 10:00 AM"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Entry Details Section */}
        <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Entry Details</h3>
          </div>
          <div className="p-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Access Instructions</label>
              <textarea
                name="entryDetails"
                value={formData.entryDetails}
                onChange={handleInputChange}
                rows="4"
                className={textareaStyles}
                placeholder="Enter instructions for accessing the property..."
              />
            </div>
          </div>
        </div>
        
        {/* Property Details Section */}
        <div className="relative z-20 bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="rounded-t-lg px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Property Details</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Beds</label>
                <input
                  type="number"
                  step="0.1"
                  name="beds"
                  value={formData.beds}
                  onChange={handleInputChange}
                  className={baseInputStyles}
                  placeholder="e.g., 3"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Baths</label>
                <input
                  type="number"
                  step="0.1"
                  name="baths"
                  value={formData.baths}
                  onChange={handleInputChange}
                  className={baseInputStyles}
                  placeholder="e.g., 2.5"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          <TeamAssignment
            users={propertyManagers}
            teamId={formData.propertyManagementTeamId}
            managerId={formData.propertyManagerId}
            selectClassName={selectStyles}
            onAssignmentChange={(assignment) => setFormData((prev) => ({ ...prev, ...assignment }))}
            cleaningAssignedUserId={formData.cleaningAssignedUserId}
            inspectionAssignedUserId={formData.inspectionAssignedUserId}
            walkthroughAssignedUserId={formData.walkthroughAssignedUserId}
            area={formData.area}
            allUsers={usersData?.users || []}
            onInputChange={handleInputChange}
          />
        </div>
        
        {/* Notes Section */}
        <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Notes</h3>
          </div>
          <div className="p-6 space-y-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Housekeeping Notes</label>
              <textarea
                name="housekeepingNotes"
                value={formData.housekeepingNotes}
                onChange={handleInputChange}
                rows="3"
                className={textareaStyles}
                placeholder="Notes for housekeeping staff..."
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Staging Notes</label>
              <textarea
                name="stagingNotes"
                value={formData.stagingNotes}
                onChange={handleInputChange}
                rows="3"
                className={textareaStyles}
                placeholder="Notes for property staging..."
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Property Manager Notes</label>
              <textarea
                name="propertyManagerNotes"
                value={formData.propertyManagerNotes}
                onChange={handleInputChange}
                rows="3"
                className={textareaStyles}
                placeholder="Notes for property managers..."
              />
            </div>
          </div>
        </div>
        
        {/* Additional Info Section */}
        <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Additional Information</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Website Link</label>
                <input
                  type="text"
                  name="websiteLink"
                  value={formData.websiteLink}
                  onChange={handleInputChange}
                  className={baseInputStyles}
                  placeholder="e.g., https://example.com/property"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Price</label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    className={moneyInputStyles}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Payout</label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    name="payout"
                    value={formData.payout}
                    onChange={handleInputChange}
                    className={moneyInputStyles}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">External ID</label>
                <input
                  type="text"
                  name="externalId"
                  value={formData.externalId}
                  onChange={handleInputChange}
                  className={baseInputStyles}
                  placeholder="External reference ID"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Integration Toggle */}
        <div className="pt-6">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="integratedWithStreamline"
              name="integratedWithStreamline"
              checked={formData.integratedWithStreamline}
              onChange={handleInputChange}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              disabled
            />
            <label htmlFor="integratedWithStreamline" className="text-sm font-medium text-gray-700">
              Integrated with Streamline
              <span className="ml-2 text-xs text-gray-500">(Only available through Streamline integration)</span>
            </label>
          </div>
        </div>

        {/* Form submit buttons */}
        <div className="flex justify-end space-x-4 pt-8 border-t border-gray-200">
          <button
            type="button"
            onClick={handleBackToProperties}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createLoading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {createLoading ? 'Creating...' : 'Create Property'}
          </button>
        </div>
      </form>
    </div>
  );
} 